import {
  App,
  Flex,
} from 'antd'
import classNames from 'classnames'
import { uniq } from 'lodash/fp'
import React, {
  HTMLAttributes,
  useCallback,
  useState,
  DragEvent,
} from 'react'

import s from './drop-zone.module.css'

export interface DropZoneProps extends HTMLAttributes<HTMLDivElement> {
  disabled?: boolean
  accept?: string
  onDropFiles: (files: File[]) => void
}

export default function DropZone({
  children, onDropFiles, accept = '', disabled = false,
}: DropZoneProps) {
  const { message } = App.useApp()
  const [isOver, setIsOver] = useState(false)

  const handleDropFiles = useCallback((e: DragEvent<HTMLDivElement>) => {
    e.preventDefault()
    setIsOver(false)

    const acceptedTypes = accept.split(',').map(type => type.trim())
    const files = Array.from(e.dataTransfer.files)
    const acceptedFiles: File[] = []
    const rejectedFiles: File[] = []

    files.forEach(file => {
      if (acceptedTypes.some(type => file.type.match(type))) {
        acceptedFiles.push(file)
      } else {
        rejectedFiles.push(file)
      }
    })

    if (rejectedFiles.length > 0) {
      const exts = uniq(rejectedFiles.map(file => file.name.split('.').pop()))
      void message.error(`OnlyFans does not allow ${exts.join(', ')} file format.`)
    }

    if (acceptedFiles.length > 0) {
      onDropFiles(acceptedFiles)
    }
  }, [accept, onDropFiles, message])

  const handleDragOver = useCallback((e: DragEvent<HTMLDivElement>) => {
    e.preventDefault()
    setIsOver(true)
  }, [])

  const handleDragLeave = useCallback((e: DragEvent<HTMLDivElement>) => {
    e.preventDefault()
    setIsOver(false)
  }, [])

  if (disabled) return children

  return (
    <div
      className={classNames(s.DropZone, { [s.IsOver]: isOver })}
      onDragOver={handleDragOver}
      onDrop={handleDropFiles}
      onDragLeave={handleDragLeave}
    >
      {isOver && <Flex className={s.Overlay}>Drop files to upload</Flex>}
      {children}
    </div>
  )
}
